/* =============================================================================
 *  ApprovalsPanel  (Unified approvals inbox — P1: per-source dispatch)
 *
 *  Globally-mounted right side-pane listing every pending item ApprovalsContext
 *  aggregates. Each Approve/Reject/Open routes back to that domain's OWN gated
 *  function — never a generic execute that could bypass the GAds holy-rule /
 *  kill-switch or the broker capability gates:
 *    - GAds   → decideChangeSet(id, 'apply'|'reject')        (gated by writesOn)
 *    - Agent  → POST decideAgentPendingAction {id, decision} (owner + capability)
 *    - Draft  → open the composer to edit & send (review-and-send, never auto-send)
 * ============================================================================= */

import React, { useEffect, useState } from 'react';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { toast } from 'react-toastify';
import { db } from '../../firebase';
import { useApprovals, ApprovalItem, ApprovalSource } from '../contexts/ApprovalsContext';
import { useTerminalTheme } from '../hooks/useTerminalTheme';
import { decideChangeSet, setWritesEnabled } from '../pages/marketing/googleAdsApi';
import { describeOp } from '../pages/marketing/ChangeActivityPanel';
import { authFetch } from '../utils/cloudFunctionsAuth';

const FN_BASE = 'https://us-central1-web-construct-app.cloudfunctions.net';

const SOURCE_META: Record<ApprovalSource, { tag: string; color: string }> = {
  gads:         { tag: 'GADS',  color: '#5ea8d4' },
  agentPending: { tag: 'AGENT', color: '#d4a04e' },
  draftEmail:   { tag: 'DRAFT', color: '#9a8fd4' },
  agentGrants:  { tag: 'GRANT', color: '#4ec0b0' },
};

// Only GAds change-sets + agent pending actions are bulk-approvable. Drafts are
// open-only; broker grants are reviewed in their own console (TTL + scope), never
// bulk/one-tap approved — both are excluded from multi-select.
const isSelectable = (i: ApprovalItem) => i.source === 'gads' || i.source === 'agentPending';
const RISK_COLOR: Record<string, string> = { high: '#d46a6a', medium: '#d4a04e', low: '#4ec07c' };

// Money-consequence warnings shown on accounting agent actions — approving mints
// a LIVE doc. Invoices: counts toward revenue, NOT auto-sent. Bills: counts toward
// what you owe, does NOT pay anything. Recurring: approval ACTIVATES the schedule.
const ACCOUNTING_WARN: Record<string, string> = {
  'invoice.create':           '⚠ Approving mints a LIVE invoice (counts toward your numbers) — it is not sent to the client.',
  'invoice.repeating.create': '⚠ Approving ACTIVATES recurring billing from the start date — generates live invoices (not sent).',
  'bill.create':              '⚠ Approving records a LIVE bill (counts toward what you owe) — it does not pay anything.',
  'bill.repeating.create':    '⚠ Approving ACTIVATES a recurring payable from the start date — generates live bills (not paid).',
};

function relTime(ms: number): string {
  if (!ms) return '';
  const s = Math.max(0, Math.floor((Date.now() - ms) / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60); if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60); if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

const ApprovalsPanel: React.FC = () => {
  const { isOpen, closeApprovals, items, count, writesOn } = useApprovals();
  const isTerminal = useTerminalTheme();
  const [busyId, setBusyId] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulk, setBulk] = useState<{ running: boolean; done: number; total: number }>({ running: false, done: 0, total: 0 });

  // ── Mount-for-animation (matches EmailSlidingPanel) ───────────────────────
  const [shouldRender, setShouldRender] = useState(false);
  const [isActuallyOpen, setIsActuallyOpen] = useState(false);
  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      const t = setTimeout(() => setIsActuallyOpen(true), 10);
      return () => clearTimeout(t);
    }
    setIsActuallyOpen(false);
    const t = setTimeout(() => setShouldRender(false), 400);
    return () => clearTimeout(t);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') closeApprovals(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, closeApprovals]);

  // ── Per-source dispatch (each routes to its domain's gated function) ──────
  const approveGads = async (item: ApprovalItem) => {
    if (busyId) return;                                   // global single-flight
    if (!writesOn) { toast.error('Arm Google Ads writes first.'); return; }
    if (!window.confirm(`Apply "${item.title}" to the LIVE Google Ads account now?`)) return;
    setBusyId(item.id);
    try {
      const r = await decideChangeSet(item.id, 'apply');
      if (!r.ok) throw new Error(r.error || 'apply failed');
      toast.success('Applied to Google Ads.');
    } catch (e: any) { toast.error(`Approve failed: ${e?.message || 'error'}`); }
    finally { setBusyId(null); }
  };
  const rejectGads = async (item: ApprovalItem) => {
    if (busyId) return;
    setBusyId(item.id);
    try {
      const r = await decideChangeSet(item.id, 'reject');
      if (!r.ok) throw new Error(r.error || 'reject failed');
      toast.success('Rejected.');
    } catch (e: any) { toast.error(`Reject failed: ${e?.message || 'error'}`); }
    finally { setBusyId(null); }
  };
  const decideAgent = async (item: ApprovalItem, decision: 'approve' | 'deny') => {
    if (busyId) return;
    if (decision === 'approve' && !window.confirm(`Approve "${item.title}"? The agent action runs now.`)) return;
    setBusyId(item.id);
    try {
      const resp = await authFetch(`${FN_BASE}/decideAgentPendingAction`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: item.id, decision }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok || data?.ok !== true) throw new Error(data?.error || `HTTP ${resp.status}`);
      toast.success(decision === 'approve' ? 'Approved.' : 'Denied.');
    } catch (e: any) { toast.error(`${decision} failed: ${e?.message || 'error'}`); }
    finally { setBusyId(null); }
  };
  const openDraft = (item: ApprovalItem) => {
    const draft = { id: item.id, ...(item.raw || {}) };
    closeApprovals();
    // Let the pane begin closing before the composer slides in.
    setTimeout(() => window.dispatchEvent(new CustomEvent('approvals:open-draft', { detail: { draft } })), 60);
  };
  // Dismiss a draft straight from the pane. It (1) DELETES the underlying Outlook
  // /Graph draft so the draft-sync can't re-materialise it, then (2) soft-deletes
  // the local mirror (status→'deleted') as a tombstone — a backstop the sync now
  // respects even if the mailbox delete failed or the Graph message-id rotated.
  // Field names differ by origin: composer mirrors carry graphMailbox/graphMessageId,
  // sync-created docs carry mailbox/graphId — accept either. Owner-gated throughout.
  const dismissDraft = async (item: ApprovalItem) => {
    if (busyId) return;
    if (!window.confirm(`Dismiss "${item.title}"? This deletes the draft from the app AND the mailbox (not sent).`)) return;
    setBusyId(item.id);
    const raw: any = item.raw || {};
    const mailbox = String(raw.graphMailbox || raw.mailbox || raw.senderAccountId || '').trim();
    const graphId = String(raw.graphMessageId || raw.graphId || '').trim();
    let mailboxCleared = !(mailbox && graphId); // nothing to delete if it isn't Graph-backed
    try {
      if (mailbox && graphId) {
        try {
          const r = await authFetch(`${FN_BASE}/deleteGraphDraftHttp`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ mailbox, id: graphId }),
          });
          const data = await r.json().catch(() => ({}));
          mailboxCleared = r.ok && data?.ok === true;
          if (!mailboxCleared) console.warn('[dismiss] Graph draft delete failed', data);
        } catch (e) { console.warn('[dismiss] Graph draft delete threw', e); }
      }
      await updateDoc(doc(db, 'email_drafts', item.id), {
        status: 'deleted', deletedAt: serverTimestamp(), updatedAt: serverTimestamp(),
      });
      toast.success(mailboxCleared
        ? 'Draft dismissed and deleted from the mailbox.'
        : 'Dismissed from the app — the mailbox copy may still be in Drafts (already gone, or Graph declined).');
    } catch (e: any) {
      toast.error(`Dismiss failed: ${e?.message || 'error'}`);
    } finally {
      setBusyId(null);
    }
  };
  const openGrant = (item: ApprovalItem) => {
    closeApprovals();
    // Open the dedicated grant-approval console (TTL + scope + capability review).
    // A grant is a trust-boundary crossing — it is approved there deliberately,
    // never one-tap-approved from the inbox.
    setTimeout(() => window.dispatchEvent(new CustomEvent('approvals:open-agent-access', { detail: { agentUid: item.id } })), 60);
  };
  const armWrites = async () => {
    if (!window.confirm('Arm Google Ads writes? Approved changes can then modify LIVE accounts until you disarm.')) return;
    try {
      const r = await setWritesEnabled(true);
      if (!r.ok) throw new Error(r.error || 'failed');
      toast.success('Google Ads writes ARMED.');
    } catch (e: any) { toast.error(`Couldn't arm: ${e?.message || 'error'}`); }
  };

  if (!shouldRender) return null;

  // ── Multi-select + approve-all (drafts excluded — they're open-only) ──────
  const keyOf = (i: ApprovalItem) => `${i.source}:${i.id}`;
  const selectableItems = items.filter(isSelectable);
  const selCount = selectableItems.filter((i) => selected.has(keyOf(i))).length;
  const allSelected = selectableItems.length > 0 && selCount === selectableItems.length;
  const toggleSelect = (i: ApprovalItem) => setSelected((prev) => {
    const n = new Set(prev); const k = keyOf(i); if (n.has(k)) n.delete(k); else n.add(k); return n;
  });
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(selectableItems.map(keyOf)));
  const actionsDisabled = !!busyId || bulk.running;

  // Sequential bulk — each item routes through its OWN gated decide endpoint
  // (re-validated server-side per id), never a batch status flip.
  const runBulk = async (action: 'approve' | 'reject') => {
    if (actionsDisabled) return;
    const sel = selectableItems.filter((i) => selected.has(keyOf(i)));
    if (!sel.length) return;
    const gadsBlocked = action === 'approve' && !writesOn && sel.some((i) => i.source === 'gads');
    const msg = `${action === 'approve' ? 'Approve' : 'Reject'} ${sel.length} selected item(s)?`
      + (action === 'approve' ? ' This applies live Google Ads changes and runs queued agent actions.' : '')
      + (gadsBlocked ? '\n\nGoogle Ads writes are disarmed — Google Ads items will be skipped.' : '');
    if (!window.confirm(msg)) return;
    setBulk({ running: true, done: 0, total: sel.length });
    let ok = 0, failed = 0, skipped = 0;
    for (const item of sel) {
      try {
        if (item.source === 'gads') {
          if (action === 'approve' && !writesOn) { skipped++; }
          else {
            const r = await decideChangeSet(item.id, action === 'approve' ? 'apply' : 'reject');
            if (!r.ok) failed++; else ok++;
          }
        } else if (item.source === 'agentPending') {
          const resp = await authFetch(`${FN_BASE}/decideAgentPendingAction`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: item.id, decision: action === 'approve' ? 'approve' : 'deny' }),
          });
          const data = await resp.json().catch(() => ({}));
          if (!resp.ok || data?.ok !== true) failed++; else ok++;
        }
      } catch { failed++; }
      setBulk((prev) => ({ ...prev, done: prev.done + 1 }));
    }
    setBulk({ running: false, done: 0, total: 0 });
    setSelected(new Set());
    toast.success(`${action === 'approve' ? 'Approved' : 'Rejected'} ${ok}`
      + (failed ? `, ${failed} failed` : '') + (skipped ? `, ${skipped} skipped (GAds disarmed)` : '') + '.');
  };

  const hasGads = items.some((i) => i.source === 'gads');

  const renderRow = (item: ApprovalItem) => {
    const meta = SOURCE_META[item.source];
    const busy = busyId === item.id;
    const ops: any[] = Array.isArray(item.raw?.operations) ? item.raw.operations : [];
    const payload: any = item.raw?.payload || {};
    const cust = payload.customerId != null
      ? (Array.isArray(payload.customerId) ? payload.customerId.join(', ') : String(payload.customerId)) : '';
    const recips = [payload.to, payload.cc, payload.bcc, payload.recipients]
      .flat().filter(Boolean).join(', ');

    return (
      <div className="approvals-row" key={`${item.source}:${item.id}`}>
        <div className="approvals-row__top">
          {isSelectable(item) && (
            <input type="checkbox" className="approvals-row__check" checked={selected.has(keyOf(item))}
              onChange={() => toggleSelect(item)} disabled={bulk.running} aria-label="Select item" />
          )}
          <span className="approvals-row__tag" style={{ color: meta.color, borderColor: meta.color }}>{meta.tag}</span>
          <span className="approvals-row__title" title={item.title}>{item.title}</span>
          <span className="approvals-row__risk" title={`risk: ${item.risk}`} style={{ background: RISK_COLOR[item.risk] || '#888' }} />
        </div>

        {/* GAds proposal: list the operations so the change is readable. */}
        {item.source === 'gads' && ops.length > 0 && (
          <ul className="approvals-row__ops">
            {ops.slice(0, 6).map((op, i) => <li key={i}>{describeOp(op)}</li>)}
            {ops.length > 6 && <li className="approvals-row__ops-more">+{ops.length - 6} more</li>}
          </ul>
        )}
        {item.source === 'gads' && ops.length === 0 && item.raw?.plan && (
          <div className="approvals-row__summary">campaign build · {item.raw.plan?.name || 'new campaign'}</div>
        )}

        {/* Agent action: surface what it WRITES TO + email recipients (confused-deputy defence). */}
        {item.source === 'agentPending' && (cust || recips) && (
          <div className="approvals-row__detail">
            {cust && <div><span className="approvals-row__k">customer:</span> {cust}</div>}
            {recips && <div><span className="approvals-row__k">recipients:</span> {recips}</div>}
          </div>
        )}
        {/* Money-consequence warning for accounting kinds — approve mints LIVE. */}
        {item.source === 'agentPending' && ACCOUNTING_WARN[item.kind] && (
          <div className="approvals-row__warn">{ACCOUNTING_WARN[item.kind]}</div>
        )}

        {/* Broker access grant: surface what it requests; review opens the grant console. */}
        {item.source === 'agentGrants' && (
          <>
            {(item.raw?.boundCustomerId || (Array.isArray(item.raw?.capabilities) && item.raw.capabilities.length > 0)) && (
              <div className="approvals-row__detail">
                {item.raw?.boundCustomerId && <div><span className="approvals-row__k">client:</span> {String(item.raw.boundCustomerId)}</div>}
                {Array.isArray(item.raw?.capabilities) && item.raw.capabilities.length > 0 && (
                  <div><span className="approvals-row__k">requests:</span> {item.raw.capabilities.join(', ')}</div>
                )}
              </div>
            )}
            <div className="approvals-row__warn">⚠ External agent requesting access — review the scope &amp; set a time limit in the grant console before approving.</div>
          </>
        )}

        <div className="approvals-row__sub">
          {item.summary && item.source !== 'gads' && <span className="approvals-row__summary">{item.summary}</span>}
          {item.source === 'gads' && <span className="approvals-row__summary">{item.summary}</span>}
          <span className="approvals-row__time">{relTime(item.createdAtMs)}</span>
        </div>

        {/* Actions — route to the domain's own gated function. */}
        <div className="approvals-row__actions">
          {item.source === 'gads' && (
            <>
              <button type="button" className="approvals-btn approvals-btn--ok"
                disabled={actionsDisabled || !writesOn} onClick={() => approveGads(item)}
                title={writesOn ? 'Apply to the live Google Ads account' : 'Arm Google Ads writes first'}>
                {busy ? '…' : 'approve'}
              </button>
              <button type="button" className="approvals-btn approvals-btn--no" disabled={actionsDisabled} onClick={() => rejectGads(item)}>reject</button>
            </>
          )}
          {item.source === 'agentPending' && (
            <>
              <button type="button" className="approvals-btn approvals-btn--ok" disabled={actionsDisabled} onClick={() => decideAgent(item, 'approve')}>{busy ? '…' : 'approve'}</button>
              <button type="button" className="approvals-btn approvals-btn--no" disabled={actionsDisabled} onClick={() => decideAgent(item, 'deny')}>deny</button>
            </>
          )}
          {item.source === 'draftEmail' && (
            <>
              <button type="button" className="approvals-btn approvals-btn--open" disabled={actionsDisabled} onClick={() => openDraft(item)}>open &amp; send →</button>
              <button type="button" className="approvals-btn approvals-btn--no" disabled={actionsDisabled} onClick={() => dismissDraft(item)} title="Delete this draft from the app and the mailbox (not sent)">{busy ? '…' : 'dismiss'}</button>
            </>
          )}
          {item.source === 'agentGrants' && (
            <button type="button" className="approvals-btn approvals-btn--open" onClick={() => openGrant(item)}>review →</button>
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      <style>{`
        .approvals-backdrop { position: fixed; inset: 0; background: rgba(0,0,0,0.5); z-index: 1040; opacity: 1; transition: opacity 0.3s ease; }
        .approvals-backdrop.closed { opacity: 0; pointer-events: none; }
        .approvals-panel { position: fixed; top: 0; right: 0; height: 100vh; height: 100dvh; width: clamp(380px, 44vw, 660px); max-width: 100%;
          background: var(--bs-body-bg, #0c0c0c); color: var(--bs-body-color, #d4d4d4); box-shadow: -6px 0 18px rgba(0,0,0,0.35);
          z-index: 1050; display: flex; flex-direction: column; overflow: hidden;
          transform: translateX(0); transition: transform 0.4s cubic-bezier(0.77,0,0.175,1), opacity 0.3s ease; }
        .approvals-panel.closed { transform: translateX(100%); opacity: 0; pointer-events: none; }
        .approvals-panel--term { background: #0a0a0a; color: #d4d4d4; font-family: 'JetBrains Mono','SFMono-Regular',ui-monospace,monospace; border-left: 1px solid #1f1f1f; }
        .approvals-head { display: flex; align-items: center; justify-content: space-between; padding: 12px 16px; border-bottom: 1px solid var(--bs-border-color, #1f1f1f); flex-shrink: 0; }
        .approvals-head__title { font-weight: 700; font-size: 14px; }
        .approvals-head__count { margin-left: 8px; font-size: 12px; opacity: 0.7; }
        .approvals-head__close { background: none; border: none; color: inherit; font-size: 20px; line-height: 1; cursor: pointer; opacity: 0.7; }
        .approvals-head__close:hover { opacity: 1; }
        .approvals-arm { display: flex; align-items: center; justify-content: space-between; gap: 8px; padding: 8px 16px; font-size: 11px;
          background: rgba(212,106,106,0.12); color: #e6b8b8; border-bottom: 1px solid rgba(212,106,106,0.3); }
        .approvals-arm button { background: #d46a6a; color: #1a0a0a; border: none; border-radius: 3px; padding: 3px 10px; font-size: 11px; font-weight: 700; cursor: pointer; }
        .approvals-bulk { display: flex; align-items: center; justify-content: space-between; gap: 8px; padding: 7px 16px; border-bottom: 1px solid var(--bs-border-color, #1f1f1f); font-size: 12px; }
        .approvals-bulk__all { display: flex; align-items: center; gap: 7px; cursor: pointer; opacity: 0.8; margin: 0; }
        .approvals-bulk__prog { opacity: 0.8; }
        .approvals-bulk__btns { display: flex; gap: 6px; }
        .approvals-row__check { flex: 0 0 auto; cursor: pointer; }
        .approvals-list { flex: 1 1 auto; overflow-y: auto; overscroll-behavior: contain; -webkit-overflow-scrolling: touch; padding: 4px 0 calc(4px + env(safe-area-inset-bottom, 0px)); }
        .approvals-empty { padding: 40px 16px; text-align: center; opacity: 0.55; font-size: 13px; }
        .approvals-row { padding: 11px 16px; border-bottom: 1px solid var(--bs-border-color, #161616); }
        .approvals-row__top { display: flex; align-items: center; gap: 8px; }
        .approvals-row__tag { flex: 0 0 auto; font-size: 9px; font-weight: 700; letter-spacing: 0.5px; border: 1px solid; border-radius: 3px; padding: 1px 4px; }
        .approvals-row__title { flex: 1 1 auto; font-size: 13px; font-weight: 600; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .approvals-row__risk { flex: 0 0 auto; width: 8px; height: 8px; border-radius: 50%; }
        .approvals-row__ops { margin: 6px 0 0; padding-left: 16px; font-size: 11.5px; opacity: 0.85; line-height: 1.5; }
        .approvals-row__ops-more { opacity: 0.6; list-style: none; margin-left: -16px; }
        .approvals-row__detail { margin-top: 5px; font-size: 11px; opacity: 0.8; }
        .approvals-row__warn { margin-top: 6px; font-size: 11px; color: #e0b056; background: rgba(212,160,78,0.10); border: 1px solid rgba(212,160,78,0.25); border-radius: 4px; padding: 4px 8px; }
        .approvals-row__k { opacity: 0.6; }
        .approvals-row__sub { display: flex; align-items: center; justify-content: space-between; gap: 8px; margin-top: 5px; font-size: 11px; opacity: 0.6; }
        .approvals-row__summary { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .approvals-row__time { flex: 0 0 auto; }
        .approvals-row__actions { display: flex; gap: 8px; margin-top: 8px; }
        .approvals-btn { font-size: 12px; font-weight: 600; border: 1px solid; border-radius: 4px; padding: 3px 12px; background: transparent; cursor: pointer; transition: background 0.15s; }
        .approvals-btn:disabled { opacity: 0.4; cursor: not-allowed; }
        .approvals-btn--ok { color: #4ec07c; border-color: #2f6f48; }
        .approvals-btn--ok:not(:disabled):hover { background: rgba(78,192,124,0.15); }
        .approvals-btn--no { color: #d46a6a; border-color: #6f2f2f; }
        .approvals-btn--no:not(:disabled):hover { background: rgba(212,106,106,0.15); }
        .approvals-btn--open { color: #5ea8d4; border-color: #2f5a6f; }
        .approvals-btn--open:not(:disabled):hover { background: rgba(94,168,212,0.15); }
        @media (max-width: 640px) {
          .approvals-panel { width: 100%; box-shadow: none; }
          .approvals-head { padding-top: calc(12px + env(safe-area-inset-top, 0px)); }
        }
        @media (hover: none) and (pointer: coarse) {
          .approvals-btn { padding: 10px 16px; min-height: 44px; }
          .approvals-head__close { min-width: 44px; min-height: 44px; display: inline-flex; align-items: center; justify-content: center; }
          .approvals-row__actions { gap: 8px; flex-wrap: wrap; }
        }
      `}</style>

      <div className={`approvals-backdrop${isActuallyOpen ? '' : ' closed'}`} onClick={closeApprovals} />

      <div className={`approvals-panel${isTerminal ? ' approvals-panel--term' : ''}${isActuallyOpen ? '' : ' closed'}`} role="dialog" aria-label="Approvals">
        <div className="approvals-head">
          <div>
            <span className="approvals-head__title">{isTerminal ? '$ approvals' : 'Approvals'}</span>
            <span className="approvals-head__count">{count}</span>
          </div>
          <button type="button" className="approvals-head__close" onClick={closeApprovals} aria-label="Close">×</button>
        </div>

        {hasGads && !writesOn && (
          <div className="approvals-arm">
            <span>Google Ads writes are disarmed — approvals to live accounts are blocked.</span>
            <button type="button" onClick={armWrites}>arm</button>
          </div>
        )}

        {selectableItems.length > 0 && (
          <div className="approvals-bulk">
            <label className="approvals-bulk__all">
              <input type="checkbox" checked={allSelected} onChange={toggleAll} disabled={bulk.running} />
              <span>{selCount > 0 ? `${selCount} selected` : 'select all'}</span>
            </label>
            {selCount > 0 && (bulk.running
              ? <span className="approvals-bulk__prog">processing {bulk.done}/{bulk.total}…</span>
              : (
                <span className="approvals-bulk__btns">
                  <button type="button" className="approvals-btn approvals-btn--ok" disabled={actionsDisabled} onClick={() => runBulk('approve')}>approve all</button>
                  <button type="button" className="approvals-btn approvals-btn--no" disabled={actionsDisabled} onClick={() => runBulk('reject')}>reject all</button>
                  <button type="button" className="approvals-btn" onClick={() => setSelected(new Set())}>clear</button>
                </span>
              ))}
          </div>
        )}

        <div className="approvals-list">
          {items.length === 0
            ? <div className="approvals-empty">Nothing waiting on you. 🎉</div>
            : items.map(renderRow)}
        </div>
      </div>
    </>
  );
};

export default ApprovalsPanel;
